import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import type { Growler } from 'utils/types/Growler';
import type {
  GrowlerCart,
  GrowlerCartItem,
  GrowlerCartContextType,
  GrowlerCartActions,
} from 'utils/types/GrowlerCart';
import { GrowlerCartStorage } from './growlerCartStorage';

const initialCart: GrowlerCart = {
  items: [],
  totalItems: 0,
  totalValue: 0,
};

interface GrowlerCartState {
  cart: GrowlerCart;
  isOpen: boolean;
}

const getInitialState = (): GrowlerCartState => {
  const savedCart = GrowlerCartStorage.loadCart();

  return {
    cart: savedCart || initialCart,
    isOpen: false,
  };
};

const recalculateCart = (items: GrowlerCartItem[]): GrowlerCart => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue =
    Math.round(
      items.reduce(
        (sum, item) => sum + item.growler.price * item.quantity,
        0,
      ) * 100,
    ) / 100;

  return {
    items,
    totalItems,
    totalValue,
  };
};

const growlerCartReducer = (
  state: GrowlerCartState,
  action: GrowlerCartActions,
): GrowlerCartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      if (!action.payload?.growler) return state;

      const { growler } = action.payload;
      const quantity = action.payload.quantity || 1;

      const existingItemIndex = state.cart.items.findIndex(
        item => item.growler.id === growler.id,
      );

      let newItems: GrowlerCartItem[];

      if (existingItemIndex >= 0) {
        newItems = state.cart.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      } else {
        const newItem: GrowlerCartItem = {
          id: `${growler.id}-${Date.now()}`,
          growler,
          quantity,
          addedAt: new Date(),
        };
        newItems = [...state.cart.items, newItem];
      }

      return {
        ...state,
        cart: recalculateCart(newItems),
      };
    }

    case 'REMOVE_ITEM': {
      if (!action.payload?.growlerId) return state;

      const newItems = state.cart.items.filter(
        item => item.growler.id !== action.payload!.growlerId,
      );

      return {
        ...state,
        cart: recalculateCart(newItems),
      };
    }

    case 'UPDATE_QUANTITY': {
      if (!action.payload?.growlerId || !action.payload?.quantity) {
        return state;
      }

      const { growlerId, quantity } = action.payload;

      if (quantity <= 0) {
        return growlerCartReducer(state, {
          type: 'REMOVE_ITEM',
          payload: { growlerId },
        });
      }

      const newItems = state.cart.items.map(item =>
        item.growler.id === growlerId ? { ...item, quantity } : item,
      );

      return {
        ...state,
        cart: recalculateCart(newItems),
      };
    }

    case 'CLEAR_CART': {
      return {
        ...state,
        cart: initialCart,
      };
    }

    case 'TOGGLE_CART': {
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    }

    case 'OPEN_CART': {
      return {
        ...state,
        isOpen: true,
      };
    }

    case 'CLOSE_CART': {
      return {
        ...state,
        isOpen: false,
      };
    }

    default:
      return state;
  }
};

const GrowlerCartContext = createContext<GrowlerCartContextType | undefined>(
  undefined,
);

interface GrowlerCartProviderProps {
  children: ReactNode;
}

export const GrowlerCartProvider: React.FC<GrowlerCartProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(growlerCartReducer, getInitialState());
  const [, setStorageInfo] = useState({ size: 0, available: true });

  const addToCart = useCallback((growler: Growler, quantity = 1) => {
    dispatch({ type: 'ADD_ITEM', payload: { growler, quantity } });
  }, []);

  const removeFromCart = useCallback((growlerId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { growlerId } });
  }, []);

  const updateQuantity = useCallback((growlerId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { growlerId, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' });
  }, []);

  const openCart = useCallback(() => {
    dispatch({ type: 'OPEN_CART' });
  }, []);

  const closeCart = useCallback(() => {
    dispatch({ type: 'CLOSE_CART' });
  }, []);

  useEffect(() => {
    if (state.cart.items.length > 0) {
      GrowlerCartStorage.saveCart(state.cart);
    } else {
      GrowlerCartStorage.clearCart();
    }
    setStorageInfo({ size: 0, available: true });
  }, [state.cart]);

  const contextValue = useMemo(
    (): GrowlerCartContextType => ({
      cart: state.cart,
      isOpen: state.isOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleCart,
      openCart,
      closeCart,
    }),
    [
      state.cart,
      state.isOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleCart,
      openCart,
      closeCart,
    ],
  );

  return (
    <GrowlerCartContext.Provider value={contextValue}>
      {children}
    </GrowlerCartContext.Provider>
  );
};

export const useGrowlerCart = (): GrowlerCartContextType => {
  const context = useContext(GrowlerCartContext);
  if (!context) {
    throw new Error(
      'useGrowlerCart must be used within a GrowlerCartProvider',
    );
  }
  return context;
};

export default GrowlerCartContext;
